import { ColDef } from '@ag-grid-community/core';
import { PacingType } from 'shared/src/domain/line-item/line-item-types';
import { timeFormat } from '@visx/vendor/d3-time-format';
import { format } from '@visx/vendor/d3-format';
import { isNil } from 'lodash';
import { formatPercentage } from 'shared/src/domain/line-item/line-item-utils';
import { formatDate as formatDateUtil } from 'shared/src/utils/date/date-utils';
import { UTCDate } from '@date-fns/utc';

export const defaultColDef: ColDef = {
  maxWidth: 250,
  suppressHeaderContextMenu: true,
  suppressHeaderMenuButton: true
};

// util
export const formatDate = timeFormat("%b %d, '%y");
export const formatNumber = format('.2s');

export function formatCurrency(currency: number) {
  const fixed = currency.toFixed(2);
  const formatted = fixed.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `$${formatted}`;
}

export function formatCurrencyNoDecimal(currency: number) {
  const sansDec = currency.toFixed(0);
  const formatted = sansDec.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `$${formatted}`;
}

export function formatUnitCost(currency: number) {
  return `$${parseFloat(currency.toFixed(3))}`;
}

export function formatPacingType(pacing_type: PacingType | undefined) {
  switch (pacing_type) {
    case 'custom':
      return 'Custom';
    case 'lifetime':
      return 'Lifetime';
    default:
      return undefined;
  }
}

export type SetFilterValuesType<T = string> = { id: T; name: string }[];

export const percentFilter = {
  cellDataType: 'number',
  filter: 'agNumberColumnFilter',
  filterParams: {
    numberFormatter: (text: string | null) => (text === null ? undefined : Number(text) * 100),
    numberParser: (text: string | null) => (text === null ? undefined : Number(text) / 100),
    filterOptions: ['greaterThan', 'greaterThanOrEqual', 'lessThan', 'lessThanOrEqual', 'inRange']
  }
};

export const numberFilter = {
  cellDataType: 'number',
  filter: 'agNumberColumnFilter',
  filterParams: {
    allowedCharPattern: '\\d\\-\\,\\.',
    filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange']
  }
};

export const textFilter = {
  filter: 'agTextColumnFilter',
  filterParams: {
    filterOptions: ['contains', 'notContains'],
    maxNumConditions: 1
  }
};

export function numericalValue(value: number | null | undefined) {
  if (isNil(value)) return '--';
  return value.toLocaleString();
}

export function currencyValue(value: number | null | undefined, defaultValue: string = '--') {
  if (isNil(value)) return defaultValue;
  return formatCurrency(value);
}

export function percentValue(value: number | null | undefined) {
  if (isNil(value)) return '--';
  return formatPercentage(value);
}

export function dateValue(value: UTCDate | null | undefined, defaultValue: string = '--') {
  if (isNil(value)) return defaultValue;
  return formatDateUtil(value);
}
